/**
 * ItemRow - one checklist item line: number, text, its Resp chip, the sign-off
 * cells (one wide CELL bar or one square per robot) and the "⋯" N/A menu.
 * Keeps RespChip, which only this row renders.
 */
import { LINKISH, keyOf } from './svmModel';
import SignCell from './SignCell';

// Small grey "who signs this" tag (Resp # from the workbook Cover legend —
// e.g. (4)/(8) = SW&Controls / Lead Install). Kodjo's ask: the responsibility
// is visible on every item without stealing attention from the text.
function RespChip({ resp }) {
  return (
    <span
      style={{
        display: 'inline-block',
        marginLeft: 6,
        padding: '0px 6px',
        borderRadius: 8,
        background: '#F0F2EE',
        color: '#7A8471',
        fontSize: 9.5,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
      }}
    >
      Resp {resp}
    </span>
  );
}

export default function ItemRow({ zoneKey, robots, item, checks, robotFilter, narrow, onCellClick, onMenuClick }) {
  // Narrow (phone): stacked - full item text on top (no truncation), sign-off
  // cells in a wrapping flex row underneath. Desktop: the original grid row.
  if (narrow) {
    return (
      <div style={{ padding: '8px 0', borderBottom: '1px solid #F4F4F4' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <span style={{ fontSize: 11, color: '#AAA', fontVariantNumeric: 'tabular-nums', flex: '0 0 auto', paddingTop: 3 }}>
            {item.no}
          </span>
          <span style={{ fontSize: 12, color: '#333', lineHeight: 1.4, flex: 1, minWidth: 0, wordBreak: 'break-word', paddingTop: 3 }}>
            {item.text}
            {item.resp && <RespChip resp={item.resp} />}
          </span>
          <button
            type="button"
            title="Mark N/A…"
            onClick={(e) => onMenuClick(item, e)}
            style={{
              ...LINKISH,
              fontSize: 16,
              color: '#C0C0C0',
              flex: '0 0 auto',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: -6,
            }}
          >
            ⋯
          </button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 2 }}>
          {item.scope === 'cell' ? (
            <div style={{ flex: '1 1 100%' }}>
              <SignCell wide narrow check={checks.get(keyOf(zoneKey, item.no, 'CELL'))} onClick={(e) => onCellClick(item, 'CELL', e)} />
            </div>
          ) : (
            robots.map((r) => (
              <SignCell
                key={r}
                label={r}
                narrow
                check={checks.get(keyOf(zoneKey, item.no, r))}
                dimmed={robotFilter !== 'ALL' && robotFilter !== r}
                onClick={(e) => onCellClick(item, r, e)}
              />
            ))
          )}
        </div>
      </div>
    );
  }
  const gridTemplate = `minmax(0, 1fr) repeat(${robots.length}, 54px) 24px`;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: gridTemplate,
        columnGap: 4,
        alignItems: 'center',
        padding: '3px 0',
        borderBottom: '1px solid #F4F4F4',
      }}
    >
      <div style={{ display: 'flex', gap: 8, minWidth: 0, paddingRight: 10 }}>
        <span
          style={{ fontSize: 10.5, color: '#AAA', fontVariantNumeric: 'tabular-nums', flex: '0 0 24px', textAlign: 'right', paddingTop: 1 }}
        >
          {item.no}
        </span>
        <span style={{ fontSize: 11.5, color: '#333', lineHeight: 1.35 }}>
          {item.text}
          {item.resp && <RespChip resp={item.resp} />}
        </span>
      </div>
      {item.scope === 'cell' ? (
        <div style={{ gridColumn: `2 / span ${robots.length}` }}>
          <SignCell wide check={checks.get(keyOf(zoneKey, item.no, 'CELL'))} onClick={(e) => onCellClick(item, 'CELL', e)} />
        </div>
      ) : (
        robots.map((r) => (
          <SignCell
            key={r}
            label={r}
            check={checks.get(keyOf(zoneKey, item.no, r))}
            dimmed={robotFilter !== 'ALL' && robotFilter !== r}
            onClick={(e) => onCellClick(item, r, e)}
          />
        ))
      )}
      <button
        type="button"
        title="Mark N/A…"
        onClick={(e) => onMenuClick(item, e)}
        style={{ ...LINKISH, fontSize: 13, color: '#C0C0C0', textAlign: 'center', lineHeight: '26px' }}
      >
        ⋯
      </button>
    </div>
  );
}
